import type {CreateAirdropDataADto} from '@cohort/admin-schemas/airdrops';
import type {CohortADto} from '@cohort/admin-schemas/cohort';
import Button from '@cohort/merchants/components/buttons/Button';
import SelectCohort from '@cohort/merchants/components/cohorts/SelectCohort';
import {DigitalAssetVisual} from '@cohort/merchants/components/DigitalAssetVisual';
import HighlightText from '@cohort/merchants/components/HighlightText';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@cohort/merchants/components/modals/Dialog';
import {standaloneAirdropKeys} from '@cohort/merchants/hooks/api/Airdrops';
import {digitalAssetCollectionsKeys} from '@cohort/merchants/hooks/api/DigitalAssetCollections';
import {digitalAssetsKeys} from '@cohort/merchants/hooks/api/DigitalAssets';
import {useCohortMutation} from '@cohort/merchants/hooks/api/Query';
import {useCurrentDigitalAssetCollection} from '@cohort/merchants/hooks/contexts/currentDigitalAssetCollection';
import {useUserSessionStore} from '@cohort/merchants/hooks/stores/userSession';
import {notify} from '@cohort/merchants/hooks/toast';
import {createStandaloneAirdrop} from '@cohort/merchants/lib/api/Airdrops';
import {defaultNetworkErrorMessage} from '@cohort/shared/models';
import {isCohortError} from '@cohort/shared/schema/common/errors';
import {zodResolver} from '@hookform/resolvers/zod';
import {useQueryClient} from '@tanstack/react-query';
import React, {useState} from 'react';
import type {UseFormRegister} from 'react-hook-form';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {z} from 'zod';

type DigitalAssetCollectionAirdropModalProps = {
  onClose: () => void;
};

type DigitalAssetCollectionAirdropFormProps = {
  register: UseFormRegister<DigitalAssetCollectionAirdropValues>;
  setError: (error: string | null | undefined) => void;
};

const DigitalAssetCollectionAirdropSchema = z.object({
  cohortId: z.string().uuid(),
});

type DigitalAssetCollectionAirdropValues = z.infer<typeof DigitalAssetCollectionAirdropSchema>;

const DigitalAssetCollectionAirdropForm: React.FC<DigitalAssetCollectionAirdropFormProps> = ({
  register,
  setError,
}) => {
  const digitalAssetCollection = useCurrentDigitalAssetCollection();
  const {t} = useTranslation('components', {
    keyPrefix: 'digitalAssets.digitalAssetCollectionAirdropModal',
  });

  return (
    <div className="mb-4 mt-8 flex flex-col gap-4">
      <section>
        <label className="mb-1 block text-sm font-medium text-slate-700">
          {t('digitalAssetLabel')}
        </label>
        <div className="flex items-center gap-2 rounded-md border bg-slate-50 p-2">
          <DigitalAssetVisual
            size={32}
            digitalAssetImageFileKey={digitalAssetCollection.imageFileKey}
            animationFileKey={digitalAssetCollection.animationFileKey}
          />
          <p>{digitalAssetCollection.internalName}</p>
        </div>
      </section>
      <SelectCohort
        register={register}
        onChange={(cohort: CohortADto) => {
          if (cohort.usersCount === 0) {
            setError(t('noUsersInCohort'));
          } else {
            setError(null);
          }
        }}
      />
    </div>
  );
};

const DigitalAssetCollectionAirdropModal: React.FC<DigitalAssetCollectionAirdropModalProps> = ({
  onClose,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const merchantId = useUserSessionStore(store => store.merchantId!);
  const digitalAssetCollection = useCurrentDigitalAssetCollection();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null | undefined>(undefined);

  const {t} = useTranslation('components', {
    keyPrefix: 'digitalAssets.digitalAssetCollectionAirdropModal',
  });

  const {isLoading, mutate: startAirdrop} = useCohortMutation({
    mutationFn: async (data: CreateAirdropDataADto) => createStandaloneAirdrop(merchantId, data),
    notifySuccessMessage: t('notificationAirdropSuccess'),
    onError: (err: unknown) => {
      if (isCohortError(err, 'airdrop.in-progress')) {
        notify('error', t('airdropInProgressError'));
        onClose();
        return;
      }
      let errorMessage = defaultNetworkErrorMessage;
      if (err instanceof Error) {
        errorMessage = err.message;
      }
      notify('error', errorMessage);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries(standaloneAirdropKeys.airdrops);
      await queryClient.invalidateQueries(digitalAssetCollectionsKeys.digitalAssetCollections);
      await queryClient.invalidateQueries(digitalAssetsKeys.digitalAssets);
      onClose();
    },
  });

  const {register, getValues} = useForm<DigitalAssetCollectionAirdropValues>({
    resolver: zodResolver(DigitalAssetCollectionAirdropSchema),
  });

  const onSubmit = (): void => {
    const data = getValues();
    startAirdrop({
      digitalAssetCollectionIds: [digitalAssetCollection.id],
      perkIds: [],
      cohortId: data.cohortId,
    });
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="w-[--medium-modal-width]">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">{t('modalTitle')}</DialogTitle>
          <DialogDescription className="m-0 text-sm">{t('modalDescription')}</DialogDescription>
        </DialogHeader>
        <DialogBody className="mt-0 py-0">
          <DigitalAssetCollectionAirdropForm register={register} setError={setError} />
        </DialogBody>
        <DialogFooter className="flex flex-col gap-4 bg-transparent">
          {error && <HighlightText type="error" text={error} />}
          <div className="flex justify-between">
            <Button key="close-duplicate-modal" variant="secondary" onClick={onClose}>
              {t('cancelBtn')}
            </Button>
            <Button onClick={onSubmit} loading={isLoading} disabled={error !== null}>
              {t('sendBtn')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DigitalAssetCollectionAirdropModal;
